/**
 * The root layout — everything that used to be in pages/_app.tsx.
 *
 * One structural difference from the Next version, and it is the reason most of this file
 * is shorter: every child here was `dynamic(..., { ssr: false })` there, with the same
 * comment on each — "under Module Federation the shared React scope isn't initialised
 * during prerender, so a statically-imported component calling hooks throws 'Cannot read
 * properties of null (reading useState)'". There is no prerender now, so they are plain
 * imports. The guards, the bridges and the session provider all mount on the first render
 * instead of a tick later, which is one less frame of unguarded UI.
 */

import React, { Component, type ReactNode } from 'react';
import { Outlet } from 'react-router-dom';
// Order matters. legacy.css declares the `syia-legacy` layer first, so it ranks below the
// Maritime layers; shell.css is unlayered and ranks above both. See each file's header.
import './styles/legacy.css';
import '@syia-ai/maritimeai-theme';
import '@syia-ai/maritimeai-ui/styles.css';
// The design's fonts, self-hosted. The host loads them once for itself and for every remote
// that asks for them by name (agenthub-overview does).
import '@fontsource-variable/inter';
import '@fontsource-variable/inter/wght-italic.css';
import '@fontsource-variable/manrope';
import './styles/shell.css';

import { SessionProvider } from '@/context/SessionContext';
import AuthGuard from '@/components/AuthGuard';
import RouteGuard from '@/components/RouteGuard';
import RouteTracker from '@/components/RouteTracker';
import ShellNavBridge from '@/components/ShellNavBridge';
import TauriBridge from '@/components/TauriBridge';
import VesselRelay from '@/components/VesselRelay';
import StatusCard from '@/components/StatusCard';
import RouterEventsBridge from '@/compat/RouterEventsBridge';
import { reportError } from '@/observability/clientLogger';
import { isStaleChunkError, recoverFromStaleChunk, staleChunkContext } from '@/utils/chunkRecovery';

interface BoundaryState {
  hasError: boolean;
  error?: Error;
  recovering: boolean;
}

/**
 * The last line before a blank page.
 *
 * Everything below it is loaded on demand, so a document that outlived its build fails
 * here first. That case is recoverable — reload once and the current chunk names apply —
 * and is only MARKED during render, never acted on: getDerivedStateFromError runs in the
 * render phase, which is no place to navigate.
 */
class ErrorBoundary extends Component<{ children: ReactNode }, BoundaryState> {
  state: BoundaryState = { hasError: false, recovering: false };

  static getDerivedStateFromError(error: Error): BoundaryState {
    return { hasError: true, error, recovering: isStaleChunkError(error) };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const stale = isStaleChunkError(error);

    // A render crash is the one client error the window handlers never see — React
    // catches it here instead, so without this line it is unreported. A stale-chunk
    // reload is logged too: it is invisible to the user by design, and the rate of it is
    // how we would notice a deploy leaving tabs stranded more often than expected.
    reportError(error, {
      event_detail: stale ? 'stale_chunk_reload' : 'render_crash',
      app: 'shell-app',
      componentStack: errorInfo?.componentStack,
      ...(stale ? staleChunkContext(error) : {}),
    });

    // Declined — this build already had its one attempt, or there is no storage to record
    // one in. Show the card rather than leave a blank page awaiting a reload that is not
    // coming.
    if (stale && !recoverFromStaleChunk(error)) this.setState({ recovering: false });
  }

  render() {
    // A reload is already in flight; the card would only be a flash of alarm on the way out.
    if (this.state.recovering) return null;

    if (this.state.hasError) {
      return (
        <StatusCard
          title="Something Went Wrong"
          message={<>This page hit an unexpected error and couldn&apos;t finish loading.<br />Reloading usually clears it.</>}
          note={{ title: 'Need Help?', body: 'If it keeps happening, contact SIYA Support.' }}
          // Reload re-runs the page the user was already on, which is where they want to
          // be — no landing-page fallback that throws away their place.
          actions={[{ label: 'Reload Page', onClick: () => window.location.reload() }]}
        />
      );
    }

    return this.props.children;
  }
}

export default function AppShell() {
  return (
    <ErrorBoundary>
      {/* SessionProvider fetches /api/me once and is the single source of truth for
          user/tenant — it must wrap the guards, which ask it rather than decoding a JWT. */}
      <SessionProvider>
        <RouterEventsBridge />
        <TauriBridge />
        <ShellNavBridge />
        <RouteTracker />
        <VesselRelay />
        <AuthGuard>
          <RouteGuard>
            <Outlet />
          </RouteGuard>
        </AuthGuard>
      </SessionProvider>
    </ErrorBoundary>
  );
}
